import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  DatabaseZap,
  Gauge,
  Layers3,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAdminAnalytics } from "@/lib/api";
import { timeAgo } from "@/lib/formatters";

const RANGE_OPTIONS = [
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
];

const REASON_LABELS = {
  access_at_risk: "Access at risk",
  inactive_30d: "Inactive 30d",
  inventory_not_adopted: "No inventory",
  low_frequency: "Low frequency",
  no_recent_sales: "No recent sales",
  open_support_case: "Open support case",
  sync_failure: "Sync failure",
};

function riskTone(level) {
  if (level === "high") return "border-rose-200 bg-rose-50 text-rose-800";
  if (level === "medium") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-800";
}

function pipelineTone(status) {
  if (status === "degraded") return "border-rose-200 bg-rose-50 text-rose-800";
  if (status === "watching") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-800";
}

function formatDay(value, range) {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "2-digit",
    month: range === "7d" ? "short" : undefined,
  });
}

export default function Analytics() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pulse");
  const [range, setRange] = useState("30d");
  const [risk, setRisk] = useState("all");
  const [search, setSearch] = useState("");

  const analytics = useQuery({
    queryKey: ["admin-product-analytics", range, risk],
    queryFn: ({ signal }) => getAdminAnalytics({ limit: 40, range, risk }, signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const data = analytics.data;
  const riskShops = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data?.riskShops || [];
    return (data?.riskShops || []).filter((shop) =>
      `${shop.name} ${shop.planName} ${shop.riskReasons?.join(" ")}`.toLowerCase().includes(query));
  }, [data?.riskShops, search]);

  if (analytics.isLoading) {
    return <AnalyticsSkeleton />;
  }
  if (analytics.isError) {
    return (
      <div data-testid="analytics-page">
        <PageHeader eyebrow="Product intelligence" title="Analytics & usage" subtitle="Reliable product adoption, engagement and retention signals." />
        <div className="flex min-h-[420px] items-center justify-center rounded-2xl border border-rose-200 bg-white p-8 text-center">
          <div>
            <AlertTriangle className="mx-auto h-9 w-9 text-rose-500" />
            <div className="mt-3 text-sm font-semibold text-[#263B35]">Analytics could not be loaded</div>
            <div className="mt-1 text-xs text-[#6B7280]">{analytics.error?.message || "The analytics service is unavailable."}</div>
            <Button className="mt-4" size="sm" onClick={() => analytics.refetch()}>Try again</Button>
          </div>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const pipeline = data?.pipeline || {};
  const totalRisk = Object.values(data?.riskBreakdown || {}).reduce((sum, value) => sum + Number(value || 0), 0);

  return (
    <div data-testid="analytics-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <PageHeader
          eyebrow="Product intelligence"
          title="Analytics & usage"
          subtitle="Live adoption, engagement and retention signals derived from production activity."
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-[#0E5B52]/10 bg-white p-1 shadow-sm">
            {RANGE_OPTIONS.map(([value, label]) => (
              <button
                className={`rounded-lg px-3 py-2 text-[10px] font-semibold transition ${range === value ? "bg-[#0E5B52] text-white shadow-sm" : "text-[#6B7280] hover:bg-[#F0F6F1]"}`}
                key={value}
                onClick={() => setRange(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={() => analytics.refetch()} disabled={analytics.isFetching}>
            <RefreshCw className={`h-4 w-4 ${analytics.isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 rounded-xl border border-[#0E5B52]/10 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${pipeline.status === "healthy" ? "bg-emerald-50 text-emerald-700" : pipeline.status === "degraded" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
            <DatabaseZap className="h-4 w-4" />
          </span>
          <div>
            <div className="text-xs font-semibold text-[#064E3B]">Product intelligence pipeline</div>
            <div className="mt-0.5 text-[10px] text-[#6B7280]">
              {pipeline.lastRefreshAt ? `Snapshot refreshed ${timeAgo(pipeline.lastRefreshAt)}` : "Waiting for the first snapshot"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Badge variant="outline" className={`rounded-full text-[9px] ${pipelineTone(pipeline.status)}`}>{pipeline.status || "unknown"}</Badge>
          <span className="text-[10px] text-[#6B7280]">{pipeline.pendingJobs || 0} queued · {pipeline.missingSnapshots || 0} missing · {pipeline.staleSnapshots || 0} stale · {pipeline.deadLetterJobs || 0} dead-letter</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-2xl border border-[#0E5B52]/10 bg-white p-1.5 shadow-sm">
          <AnalyticsTab value="pulse" icon={Gauge} label="Product Pulse" meta={`${metrics.monthlyActive || 0} monthly active`} />
          <AnalyticsTab value="adoption" icon={Layers3} label="Adoption Lab" meta={`${data?.features?.length || 0} capabilities`} />
          <AnalyticsTab value="risk" icon={ShieldCheck} label="Risk Desk" meta={`${metrics.churnRisk || 0} high risk`} />
        </TabsList>

        <TabsContent value="pulse" className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
            <KpiCard testid="kpi-dau" label="Daily active shops" value={metrics.dailyActiveShops || 0} icon={Activity} accent="#A7F3D0" hint={`${metrics.stickinessPercent || 0}% DAU / MAU`} />
            <KpiCard testid="kpi-wau" label="Weekly active" value={metrics.weeklyActive || 0} icon={Users} accent="#D9F99D" />
            <KpiCard testid="kpi-mau" label="Monthly active" value={metrics.monthlyActive || 0} icon={Users} accent="#BAE6FD" />
            <KpiCard testid="kpi-engagement" label="Avg engagement" value={`${metrics.averageEngagement || 0}/100`} icon={Sparkles} accent="#DDD6FE" />
            <KpiCard testid="kpi-churn-risk" label="High churn risk" value={metrics.churnRisk || 0} icon={AlertTriangle} accent="#FECACA" />
          </div>

          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,.65fr)]">
            <ActivityTrend data={data?.activity || []} range={range} />
            <RiskPosture breakdown={data?.riskBreakdown || {}} total={totalRisk} onOpen={() => setActiveTab("risk")} />
          </div>
        </TabsContent>

        <TabsContent value="adoption" className="mt-5">
          <FeatureAdoption features={data?.features || []} totalShops={metrics.totalShops || 0} />
        </TabsContent>

        <TabsContent value="risk" className="mt-5 space-y-4">
          <div className="flex flex-col gap-3 rounded-xl border border-[#0E5B52]/10 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
            <PageSearchInput className="flex-1" value={search} onSearch={setSearch} placeholder="Search shop, plan or risk signal…" />
            <Select value={risk} onValueChange={setRisk}>
              <SelectTrigger className="h-10 w-full sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All risk levels</SelectItem>
                <SelectItem value="high">High risk</SelectItem>
                <SelectItem value="medium">Medium risk</SelectItem>
                <SelectItem value="low">Healthy cohort</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <RiskDesk shops={riskShops} onOpen={(shop) => navigate(`/shops/${shop.id}`)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AnalyticsTab({ icon: Icon, label, meta, value }) {
  return (
    <TabsTrigger value={value} className="group h-auto min-w-0 justify-start gap-2.5 rounded-xl px-3 py-2.5 text-left data-[state=active]:bg-[#0E5B52] data-[state=active]:text-white data-[state=active]:shadow-md sm:px-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0E5B52]/8 text-[#0E5B52] group-data-[state=active]:bg-white/10 group-data-[state=active]:text-white"><Icon className="h-4 w-4" /></span>
      <span className="min-w-0"><span className="block truncate text-[11px] font-semibold sm:text-xs">{label}</span><span className="mt-0.5 hidden truncate text-[9px] font-normal opacity-65 sm:block">{meta}</span></span>
    </TabsTrigger>
  );
}

function ActivityTrend({ data, range }) {
  return (
    <section className="rounded-2xl border border-[#0E5B52]/10 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Activity pulse</div><div className="mt-1 text-base font-semibold text-[#064E3B]">Active shops and teammates</div></div>
        <div className="flex gap-3 text-[10px] text-[#6B7280]"><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-600" />Shops</span><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-cyan-400" />Users</span></div>
      </div>
      <div className="mt-4 h-72 lg:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="active-shops" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0E5B52" stopOpacity={0.32} /><stop offset="100%" stopColor="#0E5B52" stopOpacity={0.02} /></linearGradient>
              <linearGradient id="active-users" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22D3EE" stopOpacity={0.22} /><stop offset="100%" stopColor="#22D3EE" stopOpacity={0.01} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8EFEB" />
            <XAxis dataKey="date" tickFormatter={(value) => formatDay(value, range)} minTickGap={24} tick={{ fontSize: 9, fill: "#6B7280" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#6B7280" }} axisLine={false} tickLine={false} />
            <Tooltip labelFormatter={(value) => new Date(`${value}T00:00:00Z`).toLocaleDateString()} contentStyle={{ borderRadius: 12, border: "1px solid #DDE8E3", fontSize: 11 }} />
            <Area type="monotone" dataKey="activeUsers" stroke="#22B8CF" fill="url(#active-users)" strokeWidth={2} />
            <Area type="monotone" dataKey="activeShops" stroke="#0E5B52" fill="url(#active-shops)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function RiskPosture({ breakdown, total, onOpen }) {
  const segments = [
    ["low", "Healthy", breakdown.low || 0, "bg-emerald-400"],
    ["medium", "Watch", breakdown.medium || 0, "bg-amber-400"],
    ["high", "High risk", breakdown.high || 0, "bg-rose-500"],
  ];
  return (
    <section className="relative overflow-hidden rounded-2xl bg-[#071E1A] p-5 text-white shadow-sm">
      <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55"><ShieldCheck className="h-4 w-4 text-emerald-300" /> Portfolio posture</div>
        <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-white/10">
          {segments.map(([key, , value, color]) => <div key={key} className={color} style={{ width: `${total ? (value / total) * 100 : 0}%` }} />)}
        </div>
        <div className="mt-5 space-y-2">
          {segments.map(([key, label, value, color]) => (
            <div className="flex items-center justify-between rounded-xl bg-white/[0.05] px-3 py-2.5" key={key}>
              <span className="flex items-center gap-2 text-xs text-white/70"><i className={`h-2 w-2 rounded-full ${color}`} />{label}</span>
              <span className="text-sm font-semibold">{value}</span>
            </div>
          ))}
        </div>
        <Button className="mt-5 w-full bg-white text-[#064E3B] hover:bg-white/90" size="sm" onClick={onOpen}>Open Risk Desk <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" /></Button>
      </div>
    </section>
  );
}

function FeatureAdoption({ features, totalShops }) {
  if (!totalShops) {
    return (
      <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-dashed border-[#0E5B52]/20 bg-white px-6 text-center shadow-sm">
        <div className="max-w-md">
          <Layers3 className="mx-auto h-9 w-9 text-[#0E5B52]/35" />
          <div className="mt-4 text-base font-semibold text-[#064E3B]">Adoption baseline is being established</div>
          <p className="mt-2 text-xs leading-5 text-[#6B7280]">Capability rankings will appear after the first shop snapshot is processed. No feature is labelled a leader until there is production evidence.</p>
        </div>
      </div>
    );
  }
  const leader = features[0];
  const opportunity = [...features].sort((a, b) => a.adoptionPercent - b.adoptionPercent)[0];
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_340px]">
      <section className="rounded-2xl border border-[#0E5B52]/10 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between"><div><div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Adoption ladder</div><div className="mt-1 text-base font-semibold text-[#064E3B]">Durable capability usage</div></div><Badge variant="outline" className="rounded-full text-[9px]">{totalShops} shops</Badge></div>
        <div className="mt-6 space-y-5">
          {features.map((feature, index) => (
            <div key={feature.key}>
              <div className="mb-2 flex items-end justify-between gap-3"><div><span className="mr-2 text-[9px] font-semibold text-[#9CA3AF]">{String(index + 1).padStart(2, "0")}</span><span className="text-sm font-semibold text-[#064E3B]">{feature.label}</span></div><div className="text-right"><span className="text-sm font-semibold text-[#263B35]">{feature.adoptionPercent}%</span><span className="ml-1 text-[10px] text-[#9CA3AF]">{feature.shops} shops</span></div></div>
              <div className="h-2.5 overflow-hidden rounded-full bg-[#EDF3F0]"><div className="h-full rounded-full bg-gradient-to-r from-[#0E5B52] to-[#34D399] transition-all" style={{ width: `${feature.adoptionPercent}%` }} /></div>
            </div>
          ))}
        </div>
      </section>
      <aside className="space-y-4">
        <InsightCard icon={Sparkles} eyebrow="Strongest habit" title={leader?.label || "Waiting for activity"} body={leader ? `${leader.shops} shops have produced a durable ${leader.label.toLowerCase()} signal.` : "Adoption signals appear as shops use production features."} tone="emerald" />
        <InsightCard icon={TrendingDown} eyebrow="Expansion opportunity" title={opportunity?.label || "No signal yet"} body={opportunity ? `${100 - opportunity.adoptionPercent}% of shops have not adopted this capability yet.` : "There is not enough activity to rank opportunities."} tone="amber" />
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-[10px] leading-5 text-sky-900"><strong>Evidence model:</strong> adoption is derived from persisted business records, not page views, so refreshes and accidental clicks never inflate usage.</div>
      </aside>
    </div>
  );
}

function InsightCard({ body, eyebrow, icon: Icon, title, tone }) {
  const colors = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return <div className={`rounded-xl border p-4 ${colors}`}><div className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.14em] opacity-60"><Icon className="h-4 w-4" />{eyebrow}</div><div className="mt-3 text-base font-semibold">{title}</div><p className="mt-1 text-[11px] leading-5 opacity-75">{body}</p></div>;
}

function RiskDesk({ shops, onOpen }) {
  if (!shops.length) {
    return <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-emerald-200 bg-white text-center"><div><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" /><div className="mt-3 text-sm font-semibold text-[#064E3B]">No shops in this segment</div><div className="mt-1 text-xs text-[#6B7280]">Adjust the filters or check back after the next activity cycle.</div></div></div>;
  }
  return (
    <section className="overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white shadow-sm">
      <div className="hidden grid-cols-[minmax(220px,1.4fr)_110px_120px_130px_minmax(220px,1fr)_30px] gap-4 border-b border-[#0E5B52]/8 bg-[#F7FAF8] px-4 py-3 text-[9px] font-semibold uppercase tracking-wider text-[#6B7280] lg:grid">
        <span>Shop</span><span>Engagement</span><span>Activity</span><span>Operations</span><span>Risk signals</span><span />
      </div>
      <div className="divide-y divide-[#0E5B52]/7">
        {shops.map((shop) => (
          <button onClick={() => onOpen(shop)} key={shop.id} className="grid w-full gap-3 px-4 py-4 text-left transition hover:bg-[#F7FAF8] lg:grid-cols-[minmax(220px,1.4fr)_110px_120px_130px_minmax(220px,1fr)_30px] lg:items-center lg:gap-4">
            <div className="min-w-0"><div className="flex items-center gap-2"><span className="truncate text-sm font-semibold text-[#064E3B]">{shop.name}</span><Badge variant="outline" className={`rounded-full text-[8px] ${riskTone(shop.riskLevel)}`}>{shop.riskLevel}</Badge></div><div className="mt-1 truncate text-[10px] text-[#6B7280]">{shop.planName} · {shop.accessStatus}</div></div>
            <div><div className="flex items-baseline gap-1.5"><span className="text-lg font-semibold text-[#263B35]">{shop.engagementScore}</span><span className="text-[8px] font-medium text-[#9CA3AF]">{shop.scoreVersion}</span></div><div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#EDF3F0]"><div className={`h-full rounded-full ${shop.engagementScore < 40 ? "bg-rose-500" : shop.engagementScore < 60 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${shop.engagementScore}%` }} /></div></div>
            <div className="text-[10px] text-[#6B7280]"><div className="font-medium text-[#33443F]">{shop.lastActiveAt ? timeAgo(shop.lastActiveAt) : "Never active"}</div><div className="mt-1">{shop.activeDays30} active days</div></div>
            <div className="text-[10px] text-[#6B7280]"><div>{shop.sales30} sales · 30d</div><div className="mt-1">{shop.products} products · {shop.syncStatus}</div></div>
            <div className="flex flex-wrap gap-1.5">{(shop.riskReasons || []).slice(0, 3).map((reason) => <span key={reason} className="rounded-full bg-[#F4F6F5] px-2 py-1 text-[8px] font-medium text-[#59645F]">{REASON_LABELS[reason] || reason}</span>)}</div>
            <ArrowUpRight className="hidden h-4 w-4 text-[#0E5B52]/45 lg:block" />
          </button>
        ))}
      </div>
    </section>
  );
}

function AnalyticsSkeleton() {
  return <div><PageHeader eyebrow="Product intelligence" title="Analytics & usage" subtitle="Loading production activity signals…" /><div className="grid grid-cols-2 gap-4 xl:grid-cols-5">{Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-36 rounded-xl" />)}</div><div className="mt-5 grid gap-4 xl:grid-cols-[1.7fr_.65fr]"><Skeleton className="h-[420px] rounded-2xl" /><Skeleton className="h-[420px] rounded-2xl" /></div></div>;
}
